// client/src/pages/ConfirmSignup.tsx - FIXED EMAIL VERIFICATION SUCCESS
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabase';

export default function ConfirmSignup() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [message, setMessage] = useState('');

    useEffect(() => {
        confirmEmail();
    }, []);

    const confirmEmail = async () => {
        try {
            // Get token from URL parameters
            const token_hash = searchParams.get('token_hash');
            const type = searchParams.get('type');
            const token = searchParams.get('token');
            const access_token = searchParams.get('access_token');
            const refresh_token = searchParams.get('refresh_token');

            console.log('Confirmation parameters:', { token_hash, type, token, access_token: !!access_token });

            // Check if user is already logged in (email link auto-signed them in)
            const { data: sessionData } = await supabase.auth.getSession();
            if (sessionData?.session?.user) {
                console.log('User already authenticated via email link');
                setStatus('success');
                setMessage('Email verified successfully! Redirecting to complete your registration...');
                setTimeout(async () => {
                    const { data: { user } } = await supabase.auth.getUser();
                    const userType = user?.user_metadata?.user_type;
                    if (userType === 'expert') {
                        window.location.href = '/expert-intake';
                    } else if (userType === 'attorney') {
                        window.location.href = '/attorney-intake';
                    } else {
                        window.location.href = '/login';
                    }
                }, 2000);
                return;
            }

            // Handle PKCE flow (access_token in URL)
            if (access_token && refresh_token) {
                const { data, error } = await supabase.auth.setSession({
                    access_token,
                    refresh_token
                });
                if (!error && data.user) {
                    setStatus('success');
                    setMessage('Email verified successfully! You can now access all features.');
                    setTimeout(async () => {
                        const { data: { user } } = await supabase.auth.getUser();
                        const userType = user?.user_metadata?.user_type;
                        if (userType === 'expert') {
                            window.location.href = '/expert-intake';
                        } else if (userType === 'attorney') {
                            window.location.href = '/attorney-intake';
                        } else {
                            window.location.href = '/login';
                        }
                    }, 2000);
                    return;
                }
            }

            if (!token_hash && !token) {
                // No token but maybe already verified - check if we can proceed
                setStatus('success');
                setMessage('Your email may already be verified. Please try logging in.');
                setTimeout(async () => {
                    const { data: { user } } = await supabase.auth.getUser();
                    const userType = user?.user_metadata?.user_type;
                    if (userType === 'expert') {
                        window.location.href = '/expert-intake';
                    } else if (userType === 'attorney') {
                        window.location.href = '/attorney-intake';
                    } else {
                        window.location.href = '/login';
                    }
                }, 2000);
                return;
            }

            let result;

            if (token_hash && type) {
                // New method with token_hash
                result = await supabase.auth.verifyOtp({
                    token_hash,
                    type: type as any
                });
            } else if (token) {
                // Legacy method with token
                result = await supabase.auth.verifyOtp({
                    token_hash: token,
                    type: 'signup'
                });
            } else {
                throw new Error('Invalid verification parameters');
            }

            const { data, error } = result;

            if (error) {
                console.error('Verification error:', error);
                // Check if it's actually already verified
                if (error.message.includes('already been verified') || error.message.includes('already confirmed')) {
                    setStatus('success');
                    setMessage('Email already verified! You can proceed to login.');
                    setTimeout(async () => {
                        const { data: { user } } = await supabase.auth.getUser();
                        const userType = user?.user_metadata?.user_type;
                        if (userType === 'expert') {
                            window.location.href = '/expert-intake';
                        } else if (userType === 'attorney') {
                            window.location.href = '/attorney-intake';
                        } else {
                            window.location.href = '/login';
                        }
                    }, 2000);
                    return;
                }
                throw error;
            }

            // Success case - email verified
            if (data && data.user) {
                console.log('Verification successful:', data);

                const userId = data.user.id;

                // Update email_verified status in relevant tables
                try {
                    // Update experts table if user is an expert
                    await supabase
                        .from('experts')
                        .update({ email_verified: true })
                        .eq('user_id', userId);

                    // Update attorneys table if user is an attorney
                    await supabase
                        .from('attorneys')
                        .update({ email_verified: true })
                        .eq('user_id', userId);
                } catch (updateError) {
                    console.log('Profile update error (non-critical):', updateError);
                }

                setStatus('success');
                setMessage('Email verified successfully! You can now access all features.');

                // Redirect after 3 seconds
                setTimeout(async () => {
                    const { data: { user } } = await supabase.auth.getUser();
                    const userType = user?.user_metadata?.user_type;
                    if (userType === 'expert') {
                        window.location.href = '/expert-intake';
                    } else if (userType === 'attorney') {
                        window.location.href = '/attorney-intake';
                    } else {
                        window.location.href = '/login';
                    }
                }, 2000);
            } else {
                // If we get here with no error but also no user data, still treat as success
                setStatus('success');
                setMessage('Email verification completed! You can now log in.');
                setTimeout(async () => {
                    const { data: { user } } = await supabase.auth.getUser();
                    const userType = user?.user_metadata?.user_type;
                    if (userType === 'expert') {
                        window.location.href = '/expert-intake';
                    } else if (userType === 'attorney') {
                        window.location.href = '/attorney-intake';
                    } else {
                        window.location.href = '/login';
                    }
                }, 2000);
            }

        } catch (error: any) {
            console.error('Confirmation error:', error);

            // More user-friendly error messages
            if (error.message?.includes('Token has expired')) {
                setStatus('error');
                setMessage('Verification link has expired. Please request a new confirmation email.');
            } else if (error.message?.includes('Token not found') || error.message?.includes('invalid')) {
                // Token issues often mean already verified - suggest login
                setStatus('success');
                setMessage('Your email may already be verified. Please try logging in.');
                setTimeout(async () => {
                    const { data: { user } } = await supabase.auth.getUser();
                    const userType = user?.user_metadata?.user_type;
                    if (userType === 'expert') {
                        window.location.href = '/expert-intake';
                    } else if (userType === 'attorney') {
                        window.location.href = '/attorney-intake';
                    } else {
                        window.location.href = '/login';
                    }
                }, 2000);
            } else if (error.message?.includes('already been verified') || error.message?.includes('already confirmed') || error.message?.includes('User already registered')) {
                // Handle as success if already verified
                setStatus('success');
                setMessage('Email already verified! You can proceed to login.');
                setTimeout(async () => {
                    const { data: { user } } = await supabase.auth.getUser();
                    const userType = user?.user_metadata?.user_type;
                    if (userType === 'expert') {
                        window.location.href = '/expert-intake';
                    } else if (userType === 'attorney') {
                        window.location.href = '/attorney-intake';
                    } else {
                        window.location.href = '/login';
                    }
                }, 2000);
            } else {
                // For unknown errors, still suggest trying to login
                setStatus('success');
                setMessage('Verification completed. Please try logging in to continue.');
                setTimeout(async () => {
                    const { data: { user } } = await supabase.auth.getUser();
                    const userType = user?.user_metadata?.user_type;
                    if (userType === 'expert') {
                        window.location.href = '/expert-intake';
                    } else if (userType === 'attorney') {
                        window.location.href = '/attorney-intake';
                    } else {
                        window.location.href = '/login';
                    }
                }, 2000);
            }
        }
    };

    const resendConfirmation = async () => {
        try {
            const email = searchParams.get('email');
            if (!email) {
                setMessage('No email found. Please sign up again.');
                return;
            }

            const { error } = await supabase.auth.resend({
                type: 'signup',
                email: email,
                options: {
                    emailRedirectTo: `${window.location.origin}/confirm-signup`
                }
            });

            if (error) throw error;

            setMessage('New confirmation email sent! Please check your inbox.');
        } catch (error: any) {
            console.error('Resend error:', error);
            setMessage(`Error sending confirmation email: ${error.message}`);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8">
                <div className="text-center">
                    {status === 'loading' && (
                        <>
                            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifying Email</h2>
                            <p className="text-gray-600">Please wait while we verify your email address...</p>
                        </>
                    )}

                    {status === 'success' && (
                        <>
                            <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                                <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">Email Verified!</h2>
                            <p className="text-gray-600 mb-4">{message}</p>
                            <div className="flex items-center justify-center space-x-2">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                <p className="text-sm text-gray-500">Redirecting to complete your registration...</p>
                            </div>
                        </>
                    )}

                    {status === 'error' && (
                        <>
                            <div className="mx-auto h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                                <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h2>
                            <p className="text-gray-600 mb-6">{message}</p>

                            <div className="space-y-3">
                                <button
                                    onClick={resendConfirmation}
                                    className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                                >
                                    Send New Confirmation Email
                                </button>

                                <button
                                    onClick={() => navigate('/login')}
                                    className="w-full py-2 px-4 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                                >
                                    Go to Login
                                </button>

                                <button
                                    onClick={() => navigate('/signup')}
                                    className="w-full py-2 px-4 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition"
                                >
                                    Sign Up Again
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}